import { get } from 'lodash';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

import { useDispatch } from 'react-redux';

import {
  Box,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@material-ui/core';

import Button from '../../Button/Button.component';

import { useCancelCheckIn, useRequestCheckIn } from 'hooks/client.hook';

import { setModalStatus } from 'redux/slices/modal.slice';
import { setCheckInPassId } from 'redux/slices/client.slice';

import { CHECK_IN_STATUSSES } from 'utils/constants';
import { CHECK_IN_WINDOW, getCheckInWindow } from 'utils/check-in-window';

import { useStyles } from './ClientRow.styles';

const BLOCKED_HINT =
  'Podrás hacer el check-in hasta 30 minutos antes de la hora del escaneo';

const ClientRow = ({ client, status }) => {
  const { mutate: requestCheckIn, isLoading: isRequestLoading } =
    useRequestCheckIn();
  const { mutate: cancelCheckIn, isLoading: isCancelLoading } =
    useCancelCheckIn();

  const isExtraSmallSize = useMediaQuery(theme => theme.breakpoints.down('xs'));

  const dispatch = useDispatch();

  const classes = useStyles();

  const label = {
    active: 'Enviar resultados',
    inactive: 'Deshacer envío',
  }[status];

  // Las reglas de anticipación solo aplican al check-in de "Próximos escaneos".
  // "Deshacer envío" no las tiene. Se evalúa en cada render, sin temporizador,
  // así que se actualiza cuando react-query refresca la lista.
  const checkInWindow =
    status === 'active'
      ? getCheckInWindow(client?.scheduleDate)
      : CHECK_IN_WINDOW.ALLOWED;

  const isBlocked = checkInWindow === CHECK_IN_WINDOW.BLOCKED;

  const onClickHandler = id => event => {
    event.preventDefault();
    if (status === 'active') {
      if (checkInWindow === CHECK_IN_WINDOW.BLOCKED) {
        return;
      }
      if (checkInWindow === CHECK_IN_WINDOW.CONFIRM) {
        dispatch(setCheckInPassId(id));
        dispatch(setModalStatus({ name: 'EARLY_CHECK_IN', isActive: true }));
        return;
      }
      requestCheckIn(id);
    }
    if (status === 'inactive') {
      cancelCheckIn(id);
    }
  };

  return (
    <TableRow key={client._id} className={classes.row}>
      <TableCell align="center">
        {/* El <span> no es decorativo: MUI no dispara eventos de mouse sobre un
            botón deshabilitado, así que sin él el tooltip nunca se vería, que
            es justo cuando hace falta. Con title vacío no renderiza nada. */}
        {(client.type === 'internal' || client.type === 'external') && (
          <Tooltip title={isBlocked ? BLOCKED_HINT : ''}>
            <span>
              <Button
                variant="contained"
                color="primary"
                size="small"
                // `loading` se sigue pasando para el spinner, pero el
                // `disabled` tiene que incluirlo: el Button esparce `rest`
                // después de su propio `disabled={loading}`, así que este lo
                // pisaría y un doble clic mandaría dos peticiones.
                disabled={isBlocked || isRequestLoading || isCancelLoading}
                onClick={onClickHandler(client._id)}
                loading={isRequestLoading || isCancelLoading}
              >
                {label}
              </Button>
            </span>
          </Tooltip>
        )}
        {isExtraSmallSize && (
          <Box pt={1}>
            <Typography variant="body2">
              {CHECK_IN_STATUSSES[client.type]}
            </Typography>
            {client.type === 'external' && (
              <Typography variant="caption" className={classes.spec}>
                Prueba pagada
              </Typography>
            )}
          </Box>
        )}
      </TableCell>
      {!isExtraSmallSize && (
        <TableCell align="center">
          {CHECK_IN_STATUSSES[client.type]}
          <br />
          {client.type === 'external' && (
            <Typography variant="caption" className={classes.spec}>
              Prueba pagada
            </Typography>
          )}
        </TableCell>
      )}
      <TableCell align="center">
        <Typography variant="body2">
          {get(client, 'patient.name', ' ')}&nbsp;
          {get(client, 'patient.lastName', '')}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Typography variant="body2">
          {client?.type === 'fitpass'
            ? `** **** ${client?.patient?.phoneNumber?.slice(-4)}`
            : `** **** ${client?.patient?.phoneNumber}`}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Typography variant="body2">
          {format(new Date(client?.scheduleDate ?? null), "EEEEEE',' PP',' p", {
            locale: es,
          })}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

export default ClientRow;
